import { Box, Button, colors, EmptyState } from "@procore/core-react";
import { useTranslation } from "react-i18next";

import ModelLoadErrorIllustration from "media/icons/empty.svg?react";
import { isIpad, isIphone } from "utils/misc";

import { ErrorKind } from "./types";
import { isUnsupportedSafari } from "./unsupportedBrowser";

const MODEL_UNAVAILABLE = new Set<ErrorKind>([
    ErrorKind.INVALID_SCENE,
    ErrorKind.DELETED_SCENE,
    ErrorKind.EMPTY_PROJECT,
]);

interface ProcoreSceneErrorProps {
    error: ErrorKind;
    onReload: () => void;
    /** Omitted when there is no Procore project to return to, which hides the action. */
    onGoProjectHome?: () => void;
}

export function ProcoreSceneError({ error, onReload, onGoProjectHome }: ProcoreSceneErrorProps) {
    const { t } = useTranslation();
    const unsupportedSafari = isUnsupportedSafari(isIpad || isIphone);

    // Returns the single call to action for this error, or null when the error
    // leaves the user nothing useful to do. EmptyState.Actions owns the spacing.
    const renderAction = () => {
        // Reloading cannot bring back a scene that is invalid, deleted or empty, so
        // leaving is the only move worth offering.
        if (MODEL_UNAVAILABLE.has(error)) {
            return onGoProjectHome ? (
                <Button variant="primary" onClick={onGoProjectHome}>
                    {t("procoreSceneError.goToProjectHome")}
                </Button>
            ) : null;
        }

        // Reload would reopen the browser the copy just told the user to leave, so the
        // card deliberately ends with no action: switching browsers is the next step.
        if (error === ErrorKind.INIT_CANVAS_ERROR && unsupportedSafari) {
            return null;
        }

        // Every other kind is potentially transient, NOT_AUTHORIZED included: a Procore
        // 401/403 is either an expired session, which the host app renews on reload, or a
        // missing model permission, which nothing on this card can grant.
        return (
            <Button variant="primary" onClick={onReload}>
                {t("procoreSceneError.reload")}
            </Button>
        );
    };

    const getCardCopy = (errorKind: ErrorKind): [string, string] => {
        if (MODEL_UNAVAILABLE.has(errorKind)) {
            return [t("procoreSceneError.modelUnavailable.title"), t("procoreSceneError.modelUnavailable.description")];
        }

        if (errorKind === ErrorKind.NOT_AUTHORIZED) {
            return [t("procoreSceneError.notAuthorized.title"), t("procoreSceneError.notAuthorized.description")];
        }

        if (errorKind === ErrorKind.OFFLINE_UNAVAILABLE) {
            return [t("procoreSceneError.offline.title"), t("procoreSceneError.offline.description")];
        }

        if (errorKind === ErrorKind.INIT_CANVAS_ERROR) {
            return unsupportedSafari
                ? [
                      t("procoreSceneError.engineFailureSafari.title"),
                      t("procoreSceneError.engineFailureSafari.description"),
                  ]
                : [t("procoreSceneError.engineFailure.title"), t("procoreSceneError.engineFailure.description")];
        }

        return [t("procoreSceneError.unknown.title"), t("procoreSceneError.unknown.description")];
    };

    const [title, description] = getCardCopy(error);
    const action = renderAction();

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            // Overlays the canvas, which is absolutely positioned at z-index 0 and
            // would otherwise paint over an in-flow sibling.
            style={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                overflowY: "auto",
                backgroundColor: colors.white,
            }}
        >
            <EmptyState>
                <EmptyState.Image>
                    <ModelLoadErrorIllustration width={200} height={200} />
                </EmptyState.Image>
                <EmptyState.Title as="h1">{title}</EmptyState.Title>
                <EmptyState.Description>{description}</EmptyState.Description>
                {action ? <EmptyState.Actions>{action}</EmptyState.Actions> : null}
            </EmptyState>
        </Box>
    );
}
